// Pure helpers for customer-facing GROUP booking (SP3). No React — unit-testable.
//
// A group = the BOOKER (participant 0, resolved server-side from the JWT) + named
// guests, each with their OWN services. These helpers shape the participants for
// (a) the multi-customer slots query and (b) the create payload, and compute the
// group total. Single-customer booking never calls these (guests.length === 0).

const svcId = (s) => String(s?._id ?? s?.id ?? s ?? '');

/**
 * CAN THIS BOOKING CARRY ANOTHER PERSON?
 *
 * One question, asked once, by every control that would put a second person on
 * a storefront booking — the people rail, the add-person drawer, and the
 * "Book for two" CTA. It is the CAPABILITY, not a shape test: nothing here
 * counts anybody, and the answer is the same for a booking of one as for a
 * booking of five.
 *
 * Two settings, because two separate things have to be true, and the storefront
 * needs both of them for ANY second person:
 *
 *   multiCustomerEnabled  a booking may be for more than one person
 *   allowAnonymousGuests  that person may be somebody with no account
 *
 * The second one is not optional HERE, however it may look. A customer cannot
 * name another customer's record — every person this flow adds is created as an
 * anonymous guest (see `toCreateParticipants`, which sends `guestName` and never
 * a customer id), and the server refuses exactly that when the setting is off.
 *
 * `couplesEnabled` is deliberately NOT read: whether couples are OFFERED is a
 * different question from whether a second person is POSSIBLE, and conflating
 * the two is what produced a "Book for two" button that made a partner the
 * server would not accept. A couple entry asks for both answers.
 *
 * @param {object|null|undefined} salon - the public salon config (redux `getSalonReducer.data`)
 * @returns {boolean}
 */
export function canBookAnotherPerson(salon) {
  return salon?.multiCustomerEnabled === true && salon?.allowAnonymousGuests === true;
}

/**
 * Normalize a list of service items into API service legs `[{ serviceId, specialistId? }]`.
 * Each item may be a bare id/string, a service object, or an already-shaped leg.
 * `specialistId` is carried ONLY when truthy AND not the sentinel `'any'`
 * ("Any specialist" = OMITTED). We never emit `specialistId:'any'` or an empty string.
 */
const toLegs = (items) => (items || []).map((it) => {
  const serviceId = svcId(it && typeof it === 'object' ? (it.serviceId ?? it._id ?? it.id) : it);
  const specialistId = it && typeof it === 'object' ? it.specialistId : undefined;
  return (specialistId && specialistId !== 'any') ? { serviceId, specialistId } : { serviceId };
});

/** A guest row is valid when it has a name and at least one service. */
export function isGuestValid(guest) {
  return !!(guest && String(guest.name || '').trim() && Array.isArray(guest.services) && guest.services.length > 0);
}

/**
 * R4-9: single source of truth for "is this booking a couple right now?".
 *
 * LEGACY-ONLY as of the unified multi-person model. A couple's partner is now
 * an ordinary guest, so `guests` is never empty for a live couple and this
 * returns false for anything this client builds. It survives for ONE job: a
 * restored/in-flight payload carrying the old `coupled: true` boolean, which
 * the SP3 participants effect must not overwrite with guest-derived
 * participants. It no longer gates any UI — the pill rail, the add-person
 * flow, and the "Book for two" CTA are available at every arity.
 *
 * @param {object|null|undefined} obj
 * @param {Array|null|undefined} guests
 * @returns {boolean}
 */
export function isCoupleActive(obj, guests) {
  if (obj?.coupled === true) return true;
  const hasPairing = Array.isArray(obj?.pairings) && obj.pairings.length > 0;
  const noGuests = !guests || guests.length === 0;
  return hasPairing && noGuests;
}

/**
 * R6-8: canonical CONFIRM-TIME couple predicate — `coupled === true` AND EXACTLY
 * two participants. This is the STRICTER rule the ConfermationBox money/deposit
 * path needs (the card charge, the deposit split, the footer amount, the couple
 * summary all assume a booker + one partner). It differs from `isCoupleActive`
 * (which has no `length === 2` clamp and also treats a bare pairings model as a
 * couple), so the two are NOT interchangeable — `isCoupleActive` decides "is the
 * couple SURFACE on right now?" upstream, while this decides "is this a fully
 * shaped 2-person couple booking we can charge/summarize?" at confirm time.
 * Extracted so ConfermationBox stops re-inlining the same expression ~9× with
 * per-site `participants` differences (the divergence that let the deposit path
 * drift). `obj` is the booking object under test (sendBookingObj).
 *
 * @param {object|null|undefined} obj
 * @returns {boolean}
 */
export function isCoupleShape(obj) {
  return obj?.coupled === true && (obj?.participants?.length ?? 0) === 2;
}

/**
 * Slim participants for the multi-customer slots endpoint:
 * `[{ services: [{ serviceId, specialistId? }] }]` — booker first, then each valid
 * guest. `bookerServiceIds` = the ids of the booker's chosen services (the booker's
 * specialist is chosen top-level, so its legs carry no per-leg specialistId). Guest
 * legs carry `specialistId` when the guest picked one (Task 8/9).
 */
export function toSlotParticipants(bookerServiceIds, guests) {
  const booker = { services: toLegs(bookerServiceIds) };
  const guestParts = (guests || [])
    .filter(isGuestValid)
    .map((g) => ({ services: toLegs(g.services) }));
  return [booker, ...guestParts];
}

/**
 * Participants for the CREATE payload. Booker is participant 0 with NO identity
 * (the server forces it to the caller's account); guests carry guestName +
 * optional guestPhone + their services. Guest legs carry per-service `specialistId`
 * when picked; the booker's legs never do (its specialist is top-level).
 */
export function toCreateParticipants(bookerServiceIds, guests) {
  const booker = { services: toLegs(bookerServiceIds) };
  const guestParts = (guests || [])
    .filter(isGuestValid)
    .map((g) => ({
      guestName: String(g.name).trim(),
      ...(String(g.phone || '').trim() ? { guestPhone: String(g.phone).trim() } : {}),
      services: toLegs(g.services),
    }));
  return [booker, ...guestParts];
}

/** Sum a group's price: booker services + every valid guest's service legs. */
export function groupTotal(bookerServices, guests, priceOf) {
  const price = (s) => Number(priceOf ? priceOf(s) : (s?.price ?? 0)) || 0;
  let total = (bookerServices || []).reduce((sum, s) => sum + price(s), 0);
  for (const g of (guests || []).filter(isGuestValid)) {
    for (const leg of g.services) total += price(leg.serviceId);
  }
  return total;
}

// —— Rooms feature [P2, web-store]: couples toggle (2-singles vs 1-double) ——
//
// A group of EXACTLY 2 (booker + 1 valid guest) with rooms enabled and at
// least one room-attached service in the group may offer "Two separate
// rooms" (roomPreference 'singles', default) vs "One shared room together"
// ('double'). Groups of 1 or 3+ never show the toggle — the server's 'double'
// path only seats pairs. Whether 'double' actually FITS the day is left to
// the server (it simply returns no matching slots); this gate only decides
// whether the CONTROL is worth showing at all.

/**
 * True when the couples toggle should render for the current group.
 *
 * @param {boolean} roomsEnabled - salon.rooms.enabled
 * @param {number} guestCount - number of VALID guests (not counting the booker)
 * @param {string[]} bookerServiceIds
 * @param {Array<{services: Array<{serviceId}>}>} guests - raw guest rows (filtered internally)
 * @param {(id: string) => boolean} isRoomAttached - looks up a serviceId → roomAttached
 */
export function shouldShowRoomsToggle(roomsEnabled, bookerServiceIds, guests, isRoomAttached) {
  if (!roomsEnabled || typeof isRoomAttached !== 'function') return false;
  const validGuests = (guests || []).filter(isGuestValid);
  // Exactly 2 participants total = booker + exactly 1 valid guest.
  if (validGuests.length !== 1) return false;
  const allServiceIds = [
    ...(bookerServiceIds || []).map(svcId),
    ...validGuests[0].services.map((leg) => svcId(leg.serviceId)),
  ];
  return allServiceIds.some((id) => isRoomAttached(id));
}

// —— Multi-person booking redesign [Task 1/P0]: pairings + room buckets ——
//
// `pairings: Array<{ a, b }>` where a/b are participant KEYS ('booker' or a
// guest.key). One pairing = "these two share one room + synchronized start on
// distinct specialists." Invariants (enforced by moveOccupant, ASSUMED true of
// any `pairings` passed into the other helpers): each key appears in <=1
// pairing; a !== b; both keys exist among `participants`; max 2 keys per room
// (server resources are capacity-2 — see Global Constraints in the plan).
//
// `participants` is the SAME ordered array used everywhere else: index 0 is
// always the booker (identity resolved server-side from the JWT), 1..N are
// guests in order. Order is frozen — indices are derived from this array at
// send time (see `pairingsToIndices`).

/**
 * The stable key for a participant at a given position in the ordered
 * participants array. Index 0 is ALWAYS 'booker' — regardless of whatever
 * `.key` a booker-shaped object might carry — because the booker has no
 * client-assigned key (their identity is resolved server-side). Every other
 * index resolves to `participant.key` (a guest's key).
 */
export function participantKey(participant, index) {
  return index === 0 ? 'booker' : participant?.key;
}

/**
 * Every participant key present in `pairings`, mapped to its partner key.
 * Internal helper — not exported.
 */
function pairedWith(pairings) {
  const map = new Map();
  for (const { a, b } of pairings || []) {
    map.set(a, b);
    map.set(b, a);
  }
  return map;
}

/**
 * Room buckets for the "who shares a room" UI: one bucket per room, each with
 * its occupant KEYS. Paired keys share one bucket; every unpaired participant
 * gets their own single-occupant bucket. Deterministic order: rooms appear in
 * participant order, booker's room always first, and a paired room is emitted
 * once (at the position of whichever paired occupant comes first).
 *
 * @param {Array} participants - ordered participants, index 0 = booker
 * @param {Array<{a,b}>} pairings
 * @returns {Array<{ roomId: string, occupants: string[] }>}
 */
export function roomBuckets(participants, pairings) {
  const partnerOf = pairedWith(pairings);
  const keys = (participants || []).map((p, i) => participantKey(p, i));
  const seen = new Set();
  const buckets = [];
  for (const key of keys) {
    if (seen.has(key)) continue;
    const partner = partnerOf.get(key);
    if (partner && keys.includes(partner)) {
      const occupants = keys.filter((k) => k === key || k === partner);
      buckets.push({ roomId: `room-${occupants.join('-')}`, occupants });
      seen.add(key);
      seen.add(partner);
    } else {
      buckets.push({ roomId: `room-${key}`, occupants: [key] });
      seen.add(key);
    }
  }
  return buckets;
}

/**
 * Compute the NEXT `pairings` array after dragging/tapping `key` to `target`
 * (an existing bucket's `roomId`, or the sentinel `'new'` meaning "give this
 * person their own room"). Pure — does not mutate `pairings`.
 *
 * Enforces: max 2 occupants per room (a move that would create a 3rd is
 * rejected — returns `pairings` unchanged); no self-pair (moving into your
 * own current room is a no-op); each key belongs to <=1 pairing (moving an
 * already-paired key elsewhere cleanly drops its old pairing first).
 *
 * @param {Array} participants
 * @param {Array<{a,b}>} pairings
 * @param {string} key - the participant key being moved
 * @param {string} target - a roomId from `roomBuckets(...)`, or `'new'`
 * @returns {Array<{a,b}>} the next pairings array
 */
export function moveOccupant(participants, pairings, key, target) {
  const buckets = roomBuckets(participants, pairings);
  const currentRoom = buckets.find((b) => b.occupants.includes(key));
  if (!currentRoom) return pairings; // unknown key — no-op

  // Drop `key` from any existing pairing (it moves at most once).
  const withoutKey = (pairings || []).filter((p) => p.a !== key && p.b !== key);

  if (target === 'new') {
    if (currentRoom.occupants.length === 1) return pairings; // already alone
    return withoutKey;
  }

  const targetRoom = buckets.find((b) => b.roomId === target);
  if (!targetRoom) return pairings; // unknown room — no-op
  if (targetRoom.roomId === currentRoom.roomId) return pairings; // no-op: same room / self-pair

  if (targetRoom.occupants.length >= 2) return pairings; // cap-2: reject

  // Deterministic pair order: whichever of the two keys comes first in the
  // ORIGINAL participants order is `a` — keeps output stable regardless of
  // which side initiated the move.
  const [partner] = targetRoom.occupants;
  const order = (participants || []).map((p, i) => participantKey(p, i));
  const [a, b] = order.indexOf(partner) <= order.indexOf(key) ? [partner, key] : [key, partner];
  return [...withoutKey, { a, b }];
}

/**
 * Directly put two people in one room — the intuitive "Share a room with {X}"
 * action from the chooser. Unlike `moveOccupant` (which only joins an OPEN
 * room), this pairs `aKey` with `bKey` regardless of their current rooms: each
 * is first freed from any existing pairing (so a partner who was in a full room
 * is left in their own room), then the new pair {a,b} is added. Deterministic
 * pair order (earlier participant = `a`). No-op on self-pair, unknown key, or an
 * already-existing identical pair.
 */
export function pairWith(participants, pairings, aKey, bKey) {
  if (!aKey || !bKey || aKey === bKey) return pairings;
  const order = (participants || []).map((p, i) => participantKey(p, i));
  if (!order.includes(aKey) || !order.includes(bKey)) return pairings;
  const already = (pairings || []).some(
    (p) => (p.a === aKey && p.b === bKey) || (p.a === bKey && p.b === aKey),
  );
  if (already) return pairings;
  const cleaned = (pairings || []).filter(
    (p) => p.a !== aKey && p.b !== aKey && p.a !== bKey && p.b !== bKey,
  );
  const [a, b] = order.indexOf(aKey) <= order.indexOf(bKey) ? [aKey, bKey] : [bKey, aKey];
  return [...cleaned, { a, b }];
}

/**
 * Translate key-based `pairings` into index-based pairs for the wire (server
 * DTOs speak participant INDICES, not keys). Booker is always index 0.
 * Indices within a pair are sorted ascending for a stable shape regardless of
 * `a`/`b` order.
 *
 * @param {Array} participants
 * @param {Array<{a,b}>} pairings
 * @returns {Array<{ participantIndices: [number, number] }>}
 */
export function pairingsToIndices(participants, pairings) {
  const indexOf = new Map((participants || []).map((p, i) => [participantKey(p, i), i]));
  return (pairings || [])
    .map(({ a, b }) => {
      const ia = indexOf.get(a);
      const ib = indexOf.get(b);
      if (ia === undefined || ib === undefined) return null;
      return { participantIndices: ia < ib ? [ia, ib] : [ib, ia] };
    })
    .filter(Boolean);
}

/**
 * Ordered arrangement presets for a quick-pick UI: `all-separate` (empty
 * pairings) first, then one preset per valid 2-combination of participants —
 * each pairs exactly those two and leaves everyone else separate. Capped at 3
 * participants (server resources are capacity-2; beyond 3 the combinatorics
 * of "who's with whom" outgrow a flat preset list — the tap-to-move chooser
 * takes over instead).
 *
 * @param {Array} participants
 * @returns {Array<{ id: string, pairings: Array<{a,b}> }>}
 */
export function arrangementPresets(participants) {
  const keys = (participants || []).slice(0, 3).map((p, i) => participantKey(p, i));
  const presets = [{ id: 'all-separate', pairings: [] }];
  for (let i = 0; i < keys.length; i += 1) {
    for (let j = i + 1; j < keys.length; j += 1) {
      presets.push({ id: `pair-${keys[i]}-${keys[j]}`, pairings: [{ a: keys[i], b: keys[j] }] });
    }
  }
  return presets;
}

/**
 * Human prose describing the current room arrangement — the SOLE source of
 * this copy (used by the room-buckets chooser + review). Built entirely from
 * `t(key, { defaultValue, ...vars })` so it's en/he/ar-ready; interpolation
 * uses named vars (`name`, `name2`, ...), never i18next's reserved `count`.
 *
 * @param {Array} participants
 * @param {Array<{a,b}>} pairings
 * @param {(key: string, opts?: object) => string} t
 */
/**
 * English plural of a resource noun for the "separate {{noun}}s" copy. Naive
 * `+ 's'` produced "boxs"/"benchs" for a custom `resources.singularLabel`;
 * handle the common sibilant/-y endings so a configured noun reads correctly.
 * ("room"→"rooms", "box"→"boxes", "bench"→"benches", "bay"→"bays".)
 */
export function pluralizeNoun(noun) {
  const n = String(noun || 'room');
  if (/(s|x|z|ch|sh)$/i.test(n)) return `${n}es`;
  if (/[^aeiou]y$/i.test(n)) return `${n.slice(0, -1)}ies`;
  return `${n}s`;
}

export function describeArrangement(participants, pairings, t, resourceNoun = 'room', resourceNounPlural = '') {
  const translate = typeof t === 'function' ? t : (key, opts) => opts?.defaultValue ?? '';
  const noun = resourceNoun || 'room';
  // `pluralizeNoun` implements ENGLISH plural rules, so it can only be the
  // fallback. When the caller has a properly localized plural (from i18n, or a
  // salon's own label), that wins — otherwise ar/he rendered "غرفةs".
  const nounPlural = resourceNounPlural || pluralizeNoun(noun);
  // P4 (final review — Minor B): a nameless non-booker participant falls back to
  // a GENERIC translated "Guest" label — never the raw internal key (e.g.
  // 'partner'/'g1'), which would leak into customer-facing prose. Mirrors the
  // ArrangementChooser's own t('guest', { defaultValue: 'Guest' }) fallback.
  const guestLabel = translate('guest', { defaultValue: 'Guest' });
  const nameOf = new Map(
    (participants || []).map((p, i) => [participantKey(p, i), i === 0 ? (p?.name || null) : (p?.name || guestLabel)]),
  );
  const buckets = roomBuckets(participants, pairings);

  if (!pairings || pairings.length === 0) {
    return translate('booking.arrangement.allSeparate', { defaultValue: 'Everyone in separate {{nounPlural}}', noun, nounPlural });
  }

  const parts = buckets.map((bucket) => {
    if (bucket.occupants.length === 2) {
      const [k1, k2] = bucket.occupants;
      if (k1 === 'booker') {
        return translate('booking.arrangement.youShareRoom', {
          defaultValue: 'You + {{name}} share a {{noun}}',
          name: nameOf.get(k2),
          noun,
        });
      }
      return translate('booking.arrangement.pairShareRoom', {
        defaultValue: '{{name1}} + {{name2}} share a {{noun}}',
        name1: nameOf.get(k1),
        name2: nameOf.get(k2),
        noun,
      });
    }
    const [k] = bucket.occupants;
    if (k === 'booker') {
      return translate('booking.arrangement.youSeparateRoom', {
        defaultValue: 'You in a separate {{noun}}',
        noun,
      });
    }
    return translate('booking.arrangement.personSeparateRoom', {
      defaultValue: '{{name}} in a separate {{noun}}',
      name: nameOf.get(k),
      noun,
    });
  });

  return parts.join(translate('booking.arrangement.separator', { defaultValue: ' · ' }));
}

/**
 * True when the "share a room together" surface should be OFFERED at all.
 * Supersedes the legacy 4-arg `shouldShowRoomsToggle` for the redesign (that
 * export stays as-is for existing callers until Task 4 migrates them).
 *
 * @param {boolean} togetherCapable - salon supports the couplable/rooms surface
 * @param {boolean} hasCouplableOrCap2 - group has a couplable service OR a capacity-2 resource applies
 * @param {Array} participants
 */
export function shouldOfferTogether(togetherCapable, hasCouplableOrCap2, participants) {
  return !!togetherCapable && !!hasCouplableOrCap2 && (participants || []).length >= 2;
}

/**
 * The next `activePersonId` given the current one and the NEXT guests array
 * (multi-person redesign, P1). If the active person is the booker, or a guest
 * that still exists in `nextGuests`, it's kept; otherwise (the active guest was
 * removed — via ANY removal path, or the array shrank to empty) it falls back
 * to `'booker'`. Pure — the single source of truth for "keep the service grid's
 * `+` pointed at a person that actually exists", so no removal path can leave
 * `activePersonId` dangling (a dangling key makes every subsequent grid tap a
 * silent no-op in `toggleServiceForActive`).
 *
 * @param {string} activePersonId - current active id ('booker' | guest.key)
 * @param {Array<{key}>|null|undefined} nextGuests - the guests array after the change
 * @returns {string} the resolved active id
 */
export function resolveActivePersonId(activePersonId, nextGuests) {
  if (activePersonId === 'booker') return 'booker';
  const stillExists = (nextGuests || []).some((g) => g && g.key === activePersonId);
  return stillExists ? activePersonId : 'booker';
}

/**
 * R5-3: the auto-seeded 2+1 default pairing for a 3-person group. `shouldOffer‑
 * Together`/`offerArrangement` decides whether the "share a room" surface is
 * SHOWN, but that isn't enough to seed a concrete pairing: the server's co-start
 * write (§4a) forces `a0.startsAt === b0.startsAt` on EVERY seeded pairing, so
 * pairing the booker with a guest whose service is NOT couplable/room-attached
 * silently hides every non-synchronized group start (offer < write) and shows
 * "share a room" prose for a guest who gets no room.
 *
 * This pure helper seeds the booker against the FIRST guest who is ACTUALLY
 * couplable with them — both the booker (index 0) and that guest must be
 * couplable — and defaults to all-separate ([]) when no such guest exists. That
 * makes the DEFAULT flow the same gate the write enforces (offer == write).
 *
 * Only seeds for exactly 3 participants; 2-person defaults to all-separate (the
 * customer opts INTO together). Returns key-based pairings (`[{a,b}]`).
 *
 * @param {Array} participants - keyed participants (index 0 = booker)
 * @param {boolean} offerArrangement - whether the together surface is offered
 * @param {(index:number)=>boolean} isCouplableByIndex - per-participant couplability
 * @returns {Array<{a,b}>}
 */
export function seedDefaultPairings(participants, offerArrangement, isCouplableByIndex) {
  const people = Array.isArray(participants) ? participants : [];
  if (!offerArrangement || people.length !== 3) return [];
  const couplable = typeof isCouplableByIndex === 'function' ? isCouplableByIndex : () => false;
  // The booker must be couplable, else there is no valid pair to seed.
  if (!couplable(0)) return [];
  const presets = arrangementPresets(people);
  // Pair the booker with the FIRST guest (index >= 1) that is itself couplable.
  // presets[idx] is the booker+participants[idx] combo (pair (0,idx) sits at
  // preset index idx for a 3-person group).
  for (let idx = 1; idx < people.length; idx += 1) {
    if (couplable(idx)) return presets[idx]?.pairings ?? [];
  }
  return [];
}

/**
 * MAY THIS BOOKING SEND THE CARD THE CUSTOMER HAS ON FILE?
 *
 * Yes. There is no shape of booking that may not, so this takes no booking.
 *
 * What stood here was `coupled === true && participants.length === 2` — a saved
 * card was withheld from a party of three, and from any two people who had not
 * ticked "share a room together", even though the server had already deleted
 * the gate this was mirroring (`appointments.service` §5b sums the deposit over
 * every leg and has counted no heads since). The instrument a customer has on
 * file is an INSTRUMENT; it does not become a different instrument when a third
 * name is typed, and the deposit it pays is the sum of the items either way.
 *
 * It is kept as a named seam rather than inlined at the two call sites, because
 * `savedPaymentMethodId` and its `cvv` are two fields that must never disagree
 * about whether a card is riding — they were two copies of one expression once,
 * which is exactly how a card id and its CVV drift apart.
 *
 * @returns {true}
 */
export function savedCardAllowedForThisBooking() {
  return true;
}
